// src/pages/network/ConnectionsPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, Users, UserCheck, UserX, 
  Search, PlusCircle, X, Check, Clock,
  Building, Shield
} from 'lucide-react';
import { db } from '../../firebase/config';
import { 
  collection, query, where, orderBy, getDocs, 
  doc, updateDoc, deleteDoc, serverTimestamp,
  getDoc
} from 'firebase/firestore';

const ConnectionsPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [connections, setConnections] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('connections');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Redirect if not logged in
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { state: { redirectTo: '/network/connections' } });
    }
  }, [currentUser, navigate]);
  
  // Fetch connections data
  useEffect(() => {
    const fetchConnectionsData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Fetch accepted connections
        const acceptedQuery = query(
          collection(db, 'connections'),
          where('participants', 'array-contains', currentUser.uid),
          where('status', '==', 'accepted')
        );
        
        const acceptedSnap = await getDocs(acceptedQuery);
        const acceptedConnections = [];
        
        // Store business IDs to fetch their info
        const businessIds = new Set();
        
        acceptedSnap.forEach(doc => {
          const connection = doc.data();
          const otherPartyId = connection.requesterId === currentUser.uid 
            ? connection.receiverId 
            : connection.requesterId;
          
          acceptedConnections.push({
            id: doc.id,
            ...connection,
            otherPartyId
          });
          
          businessIds.add(otherPartyId);
        });
        
        // Fetch pending received requests
        const pendingQuery = query(
          collection(db, 'connections'),
          where('receiverId', '==', currentUser.uid),
          where('status', '==', 'pending')
        );
        
        const pendingSnap = await getDocs(pendingQuery);
        const pendingReqs = [];
        
        pendingSnap.forEach(doc => {
          const connection = doc.data();
          pendingReqs.push({
            id: doc.id,
            ...connection
          });
          
          businessIds.add(connection.requesterId);
        });
        
        // Fetch sent pending requests
        const sentQuery = query(
          collection(db, 'connections'),
          where('requesterId', '==', currentUser.uid),
          where('status', '==', 'pending')
        );
        
        const sentSnap = await getDocs(sentQuery);
        const sentReqs = [];
        
        sentSnap.forEach(doc => {
          const connection = doc.data();
          sentReqs.push({
            id: doc.id,
            ...connection
          });
          
          businessIds.add(connection.receiverId);
        });
        
        // Fetch business info for all connections
        const businessData = {};
        
        if (businessIds.size > 0) {
          const businessPromises = Array.from(businessIds).map(async (businessId) => {
            try {
              const businessSnap = await getDoc(doc(db, 'businesses', businessId));
              if (businessSnap.exists()) {
                return {
                  id: businessSnap.id,
                  ...businessSnap.data()
                };
              }
            } catch (err) {
              console.error(`Error fetching business ${businessId}:`, err);
            }
            return null;
          });
          
          const businesses = await Promise.all(businessPromises);
          
          businesses.forEach(business => {
            if (business) {
              businessData[business.id] = business;
            }
          });
        }
        
        // Add business data to connections
        setConnections(acceptedConnections.map(conn => ({
          ...conn,
          business: businessData[conn.otherPartyId] || null
        })));
        
        setPendingRequests(pendingReqs.map(req => ({
          ...req,
          business: businessData[req.requesterId] || null
        })));
        
        setSentRequests(sentReqs.map(req => ({
          ...req,
          business: businessData[req.receiverId] || null
        })));
        
      } catch (err) {
        console.error('Error fetching connections:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConnectionsData();
  }, [currentUser]);
  
  // Accept a connection request
  const handleAcceptRequest = async (connectionId) => {
    try {
      await updateDoc(doc(db, 'connections', connectionId), {
        status: 'accepted',
        acceptedAt: serverTimestamp()
      });
      
      // Update local state
      const acceptedRequest = pendingRequests.find(req => req.id === connectionId);
      
      if (acceptedRequest) {
        setPendingRequests(prev => prev.filter(req => req.id !== connectionId));
        setConnections(prev => [
          ...prev,
          {
            ...acceptedRequest,
            status: 'accepted',
            otherPartyId: acceptedRequest.requesterId
          }
        ]);
      }
      
    } catch (err) {
      console.error('Error accepting connection:', err);
    }
  };
  
  // Decline a connection request
  const handleDeclineRequest = async (connectionId) => {
    try {
      await deleteDoc(doc(db, 'connections', connectionId));
      
      // Update local state
      setPendingRequests(prev => prev.filter(req => req.id !== connectionId));
      
    } catch (err) {
      console.error('Error declining connection:', err);
    }
  };
  
  // Cancel a sent request
  const handleCancelRequest = async (connectionId) => {
    try {
      await deleteDoc(doc(db, 'connections', connectionId));
      
      // Update local state
      setSentRequests(prev => prev.filter(req => req.id !== connectionId));
      
    } catch (err) {
      console.error('Error canceling request:', err);
    }
  };
  
  // Remove a connection
  const handleRemoveConnection = async (connectionId) => {
    try {
      await deleteDoc(doc(db, 'connections', connectionId));
      
      // Update local state
      setConnections(prev => prev.filter(conn => conn.id !== connectionId));
      
    } catch (err) {
      console.error('Error removing connection:', err);
    }
  };
  
  // Filter connections by search term
  const getFilteredConnections = () => {
    if (!searchTerm) return connections;
    
    return connections.filter(conn => 
      conn.business?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conn.business?.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  if (!currentUser) {
    return null; // Handled by the redirect in useEffect
  }
  
  return (
    <div className="container mx-auto max-w-6xl px-4">
      <div className="mb-6">
        <Link 
          to="/network"
          className="text-[#190192] hover:text-[#2C0DB5] inline-flex items-center"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Network
        </Link>
      </div>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">My Connections</h1>
          <p className="text-gray-600">Manage your business network connections</p>
        </div>
        
        {/* Tabs Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('connections')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'connections' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Users size={16} className="mr-2" />
                Connections
                {connections.length > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
                    {connections.length}
                  </span>
                )}
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'pending' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <UserCheck size={16} className="mr-2" />
                Pending Requests
                {pendingRequests.length > 0 && (
                  <span className="ml-2 bg-red-100 text-red-700 rounded-full px-2 py-0.5 text-xs">
                    {pendingRequests.length}
                  </span>
                )}
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab('sent')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'sent' 
                  ? 'border-[#190192] text-[#190192]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Clock size={16} className="mr-2" />
                Sent Requests
                {sentRequests.length > 0 && (
                  <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs">
                    {sentRequests.length}
                  </span>
                )}
              </div>
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {/* Connections Tab */}
          {activeTab === 'connections' && (
            <div>
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    className="pl-10 pr-4 py-2.5 w-full rounded-lg text-sm border border-gray-200 focus:border-[#190192] focus:ring-2 focus:ring-[#190192] focus:ring-opacity-20"
                    placeholder="Search connections..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {/* Connections List */}
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
                </div>
              ) : connections.length === 0 ? (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No connections yet</h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-6">
                    Start building your business network by connecting with other businesses.
                  </p>
                  <Link
                    to="/network/discover"
                    className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
                  >
                    <PlusCircle size={16} className="mr-2" />
                    Find Businesses to Connect
                  </Link>
                </div>
              ) : getFilteredConnections().length === 0 ? (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No matching connections</h3>
                  <p className="text-gray-500 mb-4">
                    No connections match your search criteria.
                  </p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Clear Search
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getFilteredConnections().map(connection => (
                    <div 
                      key={connection.id}
                      className="border border-gray-200 rounded-xl overflow-hidden flex flex-col"
                    >
                      <div className="p-4 flex">
                        <div className="w-16 h-16 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                          {connection.business?.businessLogoUrl ? (
                            <img 
                              src={connection.business.businessLogoUrl} 
                              alt={connection.business.name} 
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                              <Building size={24} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/network/profile/${connection.otherPartyId}`}
                            className="text-gray-900 font-medium hover:text-[#190192]"
                          >
                            {connection.business?.name || 'Unknown Business'}
                          </Link>
                          <p className="text-gray-500 text-sm">{connection.business?.industry || 'Business'}</p>
                          <div className="mt-1 flex items-center text-xs text-gray-500">
                            <Shield size={12} className="mr-1" />
                            <span>Connected</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-auto border-t border-gray-100 p-3 flex justify-end">
                        <Link
                          to={`/network/profile/${connection.otherPartyId}`}
                          className="px-3 py-1.5 text-[#190192] text-sm font-medium rounded-lg hover:bg-purple-50"
                        >
                          View Profile
                        </Link>
                        <button
                          onClick={() => handleRemoveConnection(connection.id)}
                          className="ml-2 px-3 py-1.5 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Pending Requests Tab */}
          {activeTab === 'pending' && (
            <div>
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
                </div>
              ) : pendingRequests.length === 0 ? (
                <div className="text-center py-12">
                  <UserCheck size={48} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No pending requests</h3>
                  <p className="text-gray-500 max-w-md mx-auto">
                    You don't have any pending connection requests at this time.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map(request => (
                    <div 
                      key={request.id}
                      className="border border-gray-200 rounded-xl p-4 flex items-center"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                        {request.business?.businessLogoUrl ? (
                          <img 
                            src={request.business.businessLogoUrl} 
                            alt={request.business.name} 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                            <Building size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/network/profile/${request.requesterId}`}
                          className="text-gray-900 font-medium hover:text-[#190192]"
                        >
                          {request.business?.name || 'Unknown Business'}
                        </Link>
                        <p className="text-gray-500 text-sm">{request.business?.industry || 'Business'}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleAcceptRequest(request.id)}
                          className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                          title="Accept"
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={() => handleDeclineRequest(request.id)}
                          className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
                          title="Decline"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Sent Requests Tab */}
          {activeTab === 'sent' && (
            <div>
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin h-8 w-8 border-4 border-[#190192] rounded-full border-t-transparent"></div>
                </div>
              ) : sentRequests.length === 0 ? (
                <div className="text-center py-12">
                  <Clock size={48} className="mx-auto text-gray-300 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No sent requests</h3>
                  <p className="text-gray-500 max-w-md mx-auto mb-6">
                    You haven't sent any connection requests that are pending.
                  </p>
                  <Link
                    to="/network/discover"
                    className="px-4 py-2 bg-[#190192] text-white rounded-lg hover:bg-[#2C0DB5] inline-flex items-center"
                  >
                    <PlusCircle size={16} className="mr-2" />
                    Find Businesses to Connect
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentRequests.map(request => (
                    <div 
                      key={request.id}
                      className="border border-gray-200 rounded-xl p-4 flex items-center"
                    >
                      <div className="w-12 h-12 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                        {request.business?.businessLogoUrl ? (
                          <img 
                            src={request.business.businessLogoUrl} 
                            alt={request.business.name} 
                            className="w-full h-full object-contain"
                          />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                            <Building size={20} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/network/profile/${request.receiverId}`}
                          className="text-gray-900 font-medium hover:text-[#190192]"
                        >
                          {request.business?.name || 'Unknown Business'}
                        </Link>
                        <p className="text-gray-500 text-sm">{request.business?.industry || 'Business'}</p>
                        <div className="mt-1 flex items-center text-xs text-amber-600">
                          <Clock size={12} className="mr-1" />
                          <span>Pending</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCancelRequest(request.id)}
                        className="px-3 py-1.5 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectionsPage;